import api from './api';
import { Session, PhaseState } from '../types/session';
import { withCache, clearCache } from '../utils/apiUtils';

// Utility function for retrying requests with exponential backoff
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000,
  maxDelay: number = 10000
): Promise<T> {
  let retries = 0;
  let delay = initialDelay;

  while (true) {
    try {
      return await fn();
    } catch (error) {
      // If we've reached max retries or this isn't a rate limit error, throw
      if (
        retries >= maxRetries || 
        !error.response || 
        error.response.status !== 429
      ) {
        throw error;
      }

      // Log the retry attempt
      console.log(`Rate limit hit, retrying in ${delay}ms (attempt ${retries + 1}/${maxRetries})`);
      
      // Wait for the delay period
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Increase the delay for next attempt (exponential backoff)
      delay = Math.min(delay * 2, maxDelay);
      retries++;
    }
  }
}

export async function createSession(prompt: string): Promise<any> {
  console.log(`Creating new session with prompt: "${prompt}"`);
  
  return retryWithBackoff(async () => {
    try {
      const response = await api.post('/sessions', null, {
        params: { prompt }
      });
      console.log('Session created successfully:', response.data);
      // Return the raw response data to handle different API response structures
      return response.data;
    } catch (error) {
      console.error('Failed to create session:', error);
      throw error;
    }
  });
}

export async function getSession(sessionId: string): Promise<Session> {
  const response = await api.get(`/sessions/${sessionId}`);
  return response.data;
}

export async function advanceSession(
  sessionId: string, 
  enableReflection: boolean = false
): Promise<any> {
  console.log(`Advancing session ${sessionId}`, { enableReflection });
  try {
    const response = await api.post(`/sessions/${sessionId}/advance`, null, {
      params: { enable_reflection: enableReflection }
    });
    console.log(`Advance session response:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error advancing session:`, error);
    throw error;
  }
}

export async function getPhaseState(
  sessionId: string,
  phase: string
): Promise<PhaseState> {
  console.log(`Getting phase state for ${phase} in session ${sessionId}`);
  try {
    const response = await api.get(`/sessions/${sessionId}/phases/${phase}/state`);
    console.log(`Phase state response:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error getting phase state:`, error);
    throw error;
  }
}

export async function executePhaseAction(
  sessionId: string,
  phase: string,
  action: string,
  data: any = {}
): Promise<any> {
  console.log(`Executing phase action: ${action} for phase ${phase} in session ${sessionId}`, data);
  try {
    const response = await api.post(
      `/sessions/${sessionId}/phases/${phase}/actions/${action}`,
      data
    );
    console.log(`Phase action response:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error executing phase action:`, error);
    throw error;
  }
}

// Checkpoint Management
export async function getCheckpoints(sessionId: string): Promise<any[]> {
  const response = await api.get(`/checkpoints/${sessionId}`);
  return response.data;
}

export async function createCheckpoint(sessionId: string, description?: string): Promise<any> {
  const response = await api.post(`/checkpoints/${sessionId}`, { description });
  return response.data;
}

export async function getCheckpointData(sessionId: string, checkpointId: string): Promise<any> {
  const response = await api.get(`/checkpoints/${sessionId}/${checkpointId}`);
  return response.data;
}

export async function rollbackToCheckpoint(sessionId: string, checkpointId: string): Promise<any> {
  const response = await api.post(`/checkpoints/${checkpointId}/restore`);
  return response.data;
}

export async function exportCheckpoint(sessionId: string, checkpointId: string): Promise<void> {
  const response = await api.get(`/checkpoints/${sessionId}/${checkpointId}/export`, { 
    responseType: 'blob' 
  });
  
  // Create a download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `checkpoint-${checkpointId}.json`);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Session Forking
export async function forkSession(sessionId: string, name: string, description?: string): Promise<any> {
  const response = await api.post(`/sessions/${sessionId}/fork`, { name, description });
  return response.data;
}

// Workflow Management
export async function createWorkflow(workflow: any): Promise<any> {
  try {
    // Log the workflow data being sent to the API
    console.log("📍 WORKFLOW DATA BEING SENT TO API:", JSON.stringify(workflow, null, 2));
    
    // Log specifically the entity node positions
    if (workflow.nodes) {
      console.log("📍 ENTITY NODE POSITIONS BEING SENT:", 
        workflow.nodes.map(node => ({
          id: node.id,
          type: node.type,
          position: node.position
        }))
      );
    }
    
    // This function handles both creation and updates
    // If workflow has an ID, it will update the existing workflow
    // Otherwise, it will create a new workflow
    const response = await api.post(`/workflows`, workflow);
    
    // Log the response data
    console.log("📍 WORKFLOW RESPONSE FROM API:", JSON.stringify(response.data, null, 2));
    
    // If the response includes nodes, log their positions
    if (response.data.nodes) {
      console.log("📍 ENTITY NODE POSITIONS RECEIVED:", 
        response.data.nodes.map(node => ({
          id: node.id,
          type: node.type,
          position: node.position
        }))
      );
    }
    
    // Invalidate cache for this workflow after successful save
    if (response.data.id) {
      invalidateWorkflowCache(response.data.id);
      console.log('💾 WORKFLOW-SAVE-DEBUG: Invalidated cache for workflow:', response.data.id);
    }
    
    return response.data;
  } catch (error) {
    console.error('Failed to save workflow:', error);
    throw error;
  }
}

export async function getWorkflows(): Promise<any[]> {
  const response = await api.get(`/workflows`);
  return response.data;
}

export async function deleteWorkflow(workflowId: string): Promise<{ id: string; deleted: boolean; name: string }> {
  const response = await api.delete(`/workflows/${workflowId}`);
  invalidateWorkflowCache(workflowId);
  return response.data;
}

export async function getWorkflow(workflowId: string): Promise<any> {
  return withCache(
    `workflow_${workflowId}`,
    async () => {
      console.log(`Cache miss for workflow ${workflowId}, fetching from API`);
      const response = await api.get(`/workflows/${workflowId}`);
      return response.data;
    },
    60000 // Cache for 1 minute
  );
}

// Function to invalidate workflow cache
export function invalidateWorkflowCache(workflowId?: string): void {
  if (workflowId) {
    clearCache(`workflow_${workflowId}`);
  } else {
    clearCache(/^workflow_/);
  }
}

export async function applyWorkflow(sessionId: string, workflowId: string): Promise<any> {
  const response = await api.post(`/sessions/${sessionId}/apply_workflow/${workflowId}`);
  return response.data;
}

export async function listSessions(): Promise<any[]> {
  const response = await api.get('/sessions');
  return response.data;
}

export async function getWorkflowExecution(executionId: string): Promise<any> {
  if (!executionId) {
    console.error("getWorkflowExecution called with empty executionId");
    return null;
  }
  
  return withCache(
    `workflow_execution_${executionId}`,
    async () => {
      console.log(`Cache miss for execution ${executionId}, fetching from API`);
      try {
        const response = await api.get(`/workflows/executions/${executionId}`);
        return response.data;
      } catch (error) {
        console.error(`Error fetching workflow execution ${executionId}:`, error);
        // Don't cache errors
        throw error;
      }
    },
    5000 // Cache for 5 seconds (shorter for executions since they change more frequently)
  );
}

// Function to invalidate execution cache
export function invalidateExecutionCache(executionId?: string): void {
  if (executionId) {
    console.log(`[CACHE] Clearing cache for specific execution: ${executionId}`);
    clearCache(`workflow_execution_${executionId}`);
  } else {
    console.log(`[CACHE] Clearing ALL execution caches`);
    clearCache(/^workflow_execution_/);
  }
}
